import axios from "axios";
import { Base_URL } from "../url";

// Fetch all tickets
export const getAllTickets = async () => {
  try {
    const response = await axios.get(
      `${Base_URL}/TicketBasedOrganization/GetAllTickets`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching tickets:", error);
    throw error;
  }
};

// Fetch tickets by orgId
export const getTicketsByOrgId = async (orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/TicketBasedOrganization/GetByOrgId/${orgId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching tickets for orgId ${orgId}:`, error);
    throw error;
  }
};

// Create new ticket
export const createTicket = async (ticketData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/TicketBasedOrganization/CreateTicket`,
      ticketData
    );
    return response.data;
  } catch (error) {
    console.error("Error creating ticket:", error);
    throw error;
  }
};

// Fetch tickets by projectId
export const getTicketsByProjectId = async (projectId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/TicketBasedOrganization/GetByProjectId/${projectId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching tickets for projectId ${projectId}:`, error);
    throw error;
  }
};

// Update existing ticket
export const updateTicket = async (ticketId, ticketData) => {
  try {
    const response = await axios.put(
      `${Base_URL}/TicketBasedOrganization/UpdateTicket/${ticketId}`,
      ticketData
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating ticket ${ticketId}:`, error);
    throw error;
  }
};

// Microsoft OAuth APIs
export const microsoftOAuthApi = {
  // Start OAuth flow
  connectOrganization: async (orgId) => {
    const response = await axios.get(
      `${Base_URL}/MicrosoftOAuth/Connect/${orgId}`
    );
    return response.data;
  },

  // Check connection status
  getConnectionStatus: async (orgId) => {
    const response = await axios.get(
      `${Base_URL}/MicrosoftOAuth/Status/${orgId}`
    );
    return response.data;
  },

  // Disconnect organization
  disconnectOrganization: async (orgId) => {
    const response = await axios.delete(
      `${Base_URL}/MicrosoftOAuth/Disconnect/${orgId}`
    );
    return response.data;
  },

  // Test email connection
  testEmailConnection: async (orgId) => {
    const response = await axios.post(
      `${Base_URL}/EmailProcessing/TestConnection/${orgId}`
    );
    return response.data;
  },
};

// Organization Email APIs
export const organizationEmailApi = {
  // Get all organization emails
  getAllOrganizationEmails: async () => {
    const response = await axios.get(
      `${Base_URL}/OrganizationEmailsForTicket/GetAllOrganizationEmails`
    );
    return response.data;
  },

  // Get emails by orgId
  getEmailsByOrgId: async (orgId) => {
    const response = await axios.get(
      `${Base_URL}/OrganizationEmailsForTicket/GetByOrgId/${orgId}`
    );
    return response.data;
  },

  // Create organization email
  createOrganizationEmail: async (emailData) => {
    const response = await axios.post(
      `${Base_URL}/OrganizationEmailsForTicket/CreateOrganizationEmail`,
      emailData
    );
    return response.data;
  },

  // Create multiple organization emails
  createMultipleOrganizationEmails: async (emailsData) => {
    const response = await axios.post(
      `${Base_URL}/OrganizationEmailsForTicket/CreateMultipleOrganizationEmails`,
      emailsData
    );
    return response.data;
  },
};

// Email Processing APIs
export const emailProcessingApi = {
  // Process emails for organization
  processOrganizationEmails: async (orgId) => {
    const response = await axios.post(
      `${Base_URL}/EmailProcessing/ProcessOrganizationEmails/${orgId}`
    );
    return response.data;
  },

  // Process all organizations emails
  processAllOrganizationsEmails: async () => {
    const response = await axios.post(
      `${Base_URL}/EmailProcessing/ProcessAllOrganizationsEmails`
    );
    return response.data;
  },

  // Test email connection
  testEmailConnection: async (orgId) => {
    const response = await axios.post(
      `${Base_URL}/EmailProcessing/TestConnection/${orgId}`
    );
    return response.data;
  },
};

// Attachment APIs
export const attachmentApi = {
  // Get ticket attachments
  getTicketAttachments: async (ticketId) => {
    const response = await axios.get(
      `${Base_URL}/TicketBasedOrganization/GetTicketAttachments/${ticketId}`
    );
    return response.data;
  },

  // Download attachment
  downloadAttachment: async (blobUrl, fileName) => {
    const response = await axios.get(
      `${Base_URL}/TicketBasedOrganization/DownloadAttachment`,
      {
        params: { blobUrl, fileName },
        responseType: "blob", // Important for file downloads
      }
    );
    return response;
  },

  // View attachment in browser
  viewAttachment: async (blobUrl, fileName) => {
    const response = await axios.get(
      `${Base_URL}/TicketBasedOrganization/ViewAttachment`,
      {
        params: { blobUrl, fileName },
        responseType: "blob",
      }
    );
    return response;
  },
};

// =====================================
// 🎯 Ticket Assignment Tracking APIs
// =====================================

// ✅ Start Work
export const startWork = async (ticketId, userId, orgId) => {
  const response = await axios.post(
    `${Base_URL}/TicketAssignmentTracking/StartWork`,
    {
      TicketId: ticketId,
      AssignedUserId: userId,
      OrgId: orgId?.toString(),
    }
  );
  return response.data;
};

// ✅ Stop Work
export const stopWork = async (assignmentId) => {
  const response = await axios.post(
    `${Base_URL}/TicketAssignmentTracking/StopWork/${assignmentId}`
  );
  return response.data;
};

// ✅ Get all assignments
export const getAllAssignments = async () => {
  const response = await axios.get(
    `${Base_URL}/TicketAssignmentTracking/GetAllAssignments`
  );
  return response.data;
};

// ✅ Get assignments by ticketId
export const getAssignmentsByTicketId = async (ticketId) => {
  const response = await axios.get(
    `${Base_URL}/TicketAssignmentTracking/GetByTicketId/${ticketId}`
  );
  return response.data;
};

// ✅ Get active assignments
export const getActiveAssignments = async () => {
  const response = await axios.get(
    `${Base_URL}/TicketAssignmentTracking/GetActiveAssignments`
  );
  return response.data;
};

// ✅ Update assignment
export const updateAssignment = async (assignmentId, updateData) => {
  const response = await axios.put(
    `${Base_URL}/TicketAssignmentTracking/UpdateAssignment/${assignmentId}`,
    updateData
  );
  return response.data;
};
